import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import ProductDetailModal from '../pages//ProductsDetail';
import { 
  SlidersHorizontal, 
  Search, 
  Plus, 
  MoreVertical,
  Edit,
  Trash2,
  X,
  Download,
  Package,
  DollarSign,
  CheckCircle,
  XCircle,
  Menu,
} from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/products';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  const defaultBenefits = {
    time_credit: { amount: 0, unit: 'minutes', applicable_resources: [], never_expire: false, expiry_days: 30 },
    money_credit: { amount: 0, applicable_resources: [], never_expire: false, expiry_days: 30 },
    passes: {
      count: 0,
      is_unlimited: false,
      access_window: { days: [], start_time: '', end_time: '' },
      never_expire: false,
      expiry_days: 30
    },
    printing_credits: { amount: 0, never_expire: false, expiry_days: 30 }
  };

  const defaultAvailability = {
    purchase_type: 'one_off',
    recurring_config: { frequency: 'monthly', charge_amount: '' },
    purchase_method: 'buy_alone_or_with_plan',
    allowed_customers: 'all'
  };

  const [newProduct, setNewProduct] = useState({
    name: '',
    language: 'EN',
    product_kind: 'booking',
    description: '',
    unit_price: '',
    currency: 'IDR',
    pricing_type: 'per_quantity',
    is_refundable: false,
    benefits: defaultBenefits,
    availability: defaultAvailability
  });

  const fetchProducts = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      
      const response = await fetch(`${BASE_URL}/getProducts`, { 
        headers: { Accept: 'application/json', ...getAuthHeaders()  } 
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      
      if (!text) {
        console.warn('Empty response from server');
        setProducts([]);
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Invalid JSON response:', text);
        throw new Error('Server returned invalid JSON');
      }
      
      const productsArray = Array.isArray(data.data) ? data.data : [];
      
      const formattedProducts = productsArray.map(product => {
        // Safely map expireInDays from schema back to expire_after_days for the frontend modal components
        const b = product.benefits || {};
        const normalizedBenefits = {
          ...b,
          time_credit: { ...b.time_credit, expire_after_days: b.time_credit?.expireInDays },
          money_credit: { ...b.money_credit, expire_after_days: b.money_credit?.expireInDays },
          passes: { ...b.passes, expire_after_days: b.passes?.expireInDays },
          printing_credit: { ...b.printing_credit, expire_after_days: b.printing_credit?.expireInDays }
        };

        return {
          id: product._id,
          name: product.name || product.product_name,
          language: product.language || 'EN',
          product_kind: product.product_kind || 'booking',
          description: product.description || '',
          price: product.pricing?.unit_price ? `${product.pricing.currency} ${product.pricing.unit_price}` : 'N/A',
          currency: product.pricing?.currency || 'IDR',
          pricing_type: product.pricing?.pricing_type || 'per_quantity',
          is_refundable: product.pricing?.is_refundable || false,
          status: product.status === 'published' ? 'Published' : 'Internal',
          is_active: Boolean(product.is_active),
          purchase_type: product.availability?.purchase_type || 'one_off',
          purchase_method: product.availability?.purchase_method || 'buy_alone_or_with_plan',
          allowed_customers: product.availability?.allowed_customers || 'all',
          availability: product.availability || {}, // Pass full availability for recurring data!
          benefits: normalizedBenefits,
          rawData: product
        };
      });
      
      setProducts(formattedProducts);
      
      setSelectedProduct(prevSelected => {
        if (!prevSelected) return null;
        const updated = formattedProducts.find(p => p.id === prevSelected.id);
        return updated || prevSelected;
      });
      
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Failed to fetch products: ' + error.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch('https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/resource/viewAllResource', { 
        headers: { Accept: 'application/json', ...getAuthHeaders() } 
      });
      const data = await response.json();
      setResources(data.data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchResources();
  }, []);

  const handleBulkToggle = () => {
    setBulkMode(!bulkMode);
    setSelectedProducts([]);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProducts(filteredProducts.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} product(s)?`)) {
      return;
    }
  
    try {
      for (const productId of selectedProducts) {
        await fetch(`${BASE_URL}/deleteProducts`, { 
          method: 'DELETE',
          headers: { 
            'Content-Type': 'application/json',
            Accept: 'application/json', 
            ...getAuthHeaders() 
          },
          body: JSON.stringify({ productId })
        });
      }
      
      await fetchProducts(false);
      setSelectedProducts([]);
      setBulkMode(false);
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Failed to delete products');
    }
  };

  const handleExport = () => {
    const header = [
      'Name',
      'Language',
      'Product Kind',
      'Price',
      'Status',
      'Purchase Type',
      'Allowed Customers',
      'Description'
    ];
    
    const rows = products.map(product => {
      const plainDescription = (product.description || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    
      return [
        product.name,
        product.language,
        product.product_kind,
        product.price,
        product.status,
        product.purchase_type,
        product.allowed_customers,
        plainDescription
      ];
    });

    const csvContent = [header, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };


  const updateBenefit = (category, field, value) => {
    setNewProduct(prev => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        [category]: { ...prev.benefits[category], [field]: value }
      }
    }));
  };

  const updatePassWindow = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        passes: {
          ...prev.benefits.passes,
          access_window: { ...prev.benefits.passes.access_window, [field]: value }
        }
      }
    }));
  };

  const updateAvailability = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      availability: { ...prev.availability, [field]: value }
    }));
  };

  const updateRecurringConfig = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        recurring_config: { ...prev.availability.recurring_config, [field]: value }
      }
    }));
  };

  const handleResourceToggle = (category, resourceId) => {
    setNewProduct(prev => {
      const currentSelected = prev.benefits[category].applicable_resources || [];
      const isSelected = currentSelected.includes(resourceId);
      return {
        ...prev,
        benefits: {
          ...prev.benefits,
          [category]: {
            ...prev.benefits[category],
            applicable_resources: isSelected 
              ? currentSelected.filter(id => id !== resourceId)
              : [...currentSelected, resourceId]
          }
        }
      };
    });
  };

  const handleAddProduct = async () => {
    try {
      const productData = {
        name: newProduct.name,
        language: newProduct.language,
        product_kind: newProduct.product_kind,
        description: newProduct.description,
        pricing: {
          unit_price: parseFloat(newProduct.unit_price || 0),
          currency: newProduct.currency,
          pricing_type: newProduct.pricing_type,
          is_refundable: newProduct.is_refundable
        },
        benefits: {
          time_credit: {
            amount: parseInt(newProduct.benefits.time_credit.amount) || 0,
            unit: newProduct.benefits.time_credit.unit,
            applicable_resources: newProduct.benefits.time_credit.applicable_resources || [],
            never_expire: newProduct.benefits.time_credit.never_expire,
            expireInDays: parseInt(newProduct.benefits.time_credit.expiry_days) || 0
          },
          money_credit: {
            amount: parseInt(newProduct.benefits.money_credit.amount) || 0,
            never_expire: newProduct.benefits.money_credit.never_expire,
            expireInDays: parseInt(newProduct.benefits.money_credit.expiry_days) || 0
          },
          passes: {
            count: parseInt(newProduct.benefits.passes.count) || 0,
            is_unlimited: newProduct.benefits.passes.is_unlimited,
            access_window: newProduct.benefits.passes.access_window,
            never_expire: newProduct.benefits.passes.never_expire,
            expireInDays: parseInt(newProduct.benefits.passes.expiry_days) || 0
          },
          printing_credit: { // Matches the Mongoose Schema perfectly
            amount: parseInt(newProduct.benefits.printing_credits.amount) || 0,
            never_expire: newProduct.benefits.printing_credits.never_expire,
            expireInDays: parseInt(newProduct.benefits.printing_credits.expiry_days) || 0
          }
        },
        availability: {
          purchase_type: newProduct.availability.purchase_type,
          purchase_method: newProduct.availability.purchase_method,
          allowed_customers: newProduct.availability.allowed_customers,
          ...(newProduct.availability.purchase_type === 'recurring' ? {
            recurring_config: {
              frequency: newProduct.availability.recurring_config.frequency,
              charge_amount: parseFloat(newProduct.availability.recurring_config.charge_amount || 0)
            }
          } : {})
        },
        status: 'internal'
      };
  
      const response = await fetch(`${BASE_URL}/createProducts`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json', 
          ...getAuthHeaders() 
        },
        body: JSON.stringify(productData)
      });
  
      if (!response.ok) {
        let msg = 'Failed to create product';
        try { 
          const errorData = await response.json();
          msg = errorData.message || msg; 
        } catch {
          try { msg = await response.text() || msg; } catch {}
        }
        throw new Error(msg);
      }
  
      await fetchProducts(false);
      setShowAddProduct(false);
      resetForm();
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Failed to create product: ' + error.message);
    }
  };

  const resetForm = () => {
    setNewProduct({
      name: '',
      language: 'EN',
      product_kind: 'booking',
      description: '',
      unit_price: '',
      currency: 'IDR',
      pricing_type: 'per_quantity',
      is_refundable: false,
      benefits: defaultBenefits,
      availability: defaultAvailability
    });
    setActiveTab('details');
  };

  const openDetailView = (product) => {
    setSelectedProduct(product);
    setShowDetailView(true);
    setActiveDropdown(null);
  };

  const handleStatusChange = async (productId, newStatus) => {
    try {
      const response = await fetch(`${BASE_URL}/updateProducts/${productId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Accept: 'application/json', 
          ...getAuthHeaders() 
        },
        body: JSON.stringify({ 
          status: newStatus.toLowerCase(),
          is_active: newStatus === 'Published'
        })
      });
  
      if (!response.ok) {
        let msg = 'Failed to update status';
        try { 
          const errorData = await response.json();
          msg = errorData.message || msg; 
        } catch {}
        throw new Error(msg);
      }
  
      await fetchProducts(false);
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/deleteProducts`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders() 
        },
        body: JSON.stringify({ productId })
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      await fetchProducts(false);
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product: ' + error.message);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isFormValid = () => {
    return newProduct.name.trim() !== '' && 
           newProduct.unit_price !== '';
  };

  const formatCurrency = (amount) => {
    const cleanAmount = typeof amount === 'string' ? amount.replace(/[^0-9.-]+/g, "") : amount;
    
    const formattedNumber = parseFloat(cleanAmount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${formattedNumber}`;
  };

  // Calculate stats
  const totalProducts = products.length;
  const publishedProducts = products.filter(p => p.status === 'Published').length;
  const internalProducts = products.filter(p => p.status === 'Internal').length;
  const avgPrice = products.length > 0 
    ? Math.round(products.reduce((sum, p) => {
        const price = parseFloat(p.price.split(' ')[1]) || 0;
        return sum + price;
      }, 0) / products.length)
    : 0;

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar currentPath="/products" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar 
        currentPath="/products" 
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── Mobile top bar ── */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="h-7 w-auto object-contain mr-5"
          />
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage your products and add-ons
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Total Products</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {totalProducts}
                  </div>
                  <Package className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Published</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {publishedProducts}
                  </div>
                  <CheckCircle className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-gray-600">Internal</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-3xl font-semibold text-gray-900">
                    {internalProducts}
                  </div>
                  <XCircle className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                </div>
              </div>

              <div className="bg-[#214BAE] rounded-lg p-5 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm text-blue-100">Avg. Price</div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="text-2xl font-semibold text-white">
                    {avgPrice > 0 ? formatCurrency(avgPrice) : 'N/A'}
                  </div>
                  <p className='font-medium text-sm text-white'>IDR</p>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="p-4">
                <div className="flex items-center justify-between gap-4 overflow-x-auto pb-2">
                  {/* Search */}
                  <div className="relative flex-1 min-w-max max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-[200px] sm:w-[300px] lg:w-[400px] pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 min-w-max">
                    {bulkMode && selectedProducts.length > 0 && (
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete ({selectedProducts.length})
                      </button>
                    )}
                    <button
                      onClick={handleBulkToggle}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      {bulkMode ? 'Cancel' : 'Bulk Actions'}
                    </button>
                    <button
                      onClick={handleExport}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                    <button
                      onClick={() => setShowAddProduct(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-[#214BAE] rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Product
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {bulkMode && (
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <tr 
                          key={product.id} 
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={(e) => {
                            if (!e.target.closest('input') && !e.target.closest('button')) {
                              openDetailView(product);
                            }
                          }}
                        >
                          {bulkMode && (
                            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(product.id)}
                                onChange={() => handleSelectProduct(product.id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {product.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                              {product.product_kind}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">IDR {formatCurrency(product.price)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              product.status === 'Published'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {product.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="relative inline-block">
                              <button
                                onClick={() => setActiveDropdown(activeDropdown === product.id ? null : product.id)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>
                              
                              {activeDropdown === product.id && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                  <button
                                    onClick={() => openDetailView(product)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Edit className="w-4 h-4" />
                                    View Details
                                  </button>
                                  <button
                                    onClick={() => handleStatusChange(product.id, product.status === 'Published' ? 'Internal' : 'Published')}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    {product.status === 'Published' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                    {product.status === 'Published' ? 'Set as Internal' : 'Publish'}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProduct(product.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Product
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={bulkMode ? "6" : "5"} className="px-6 py-12 text-center text-sm text-gray-500">
                          No products found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start flex-col justify-center border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900 pl-6 pt-6">Add New Product</h2>
              <div className="flex border-b  border-gray-200 px-3 mt-2 w-full">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('details')}
              >
                Product Details
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'benefits' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('benefits')}
              >
                Benefits
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === 'availability' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('availability')}
              >
                Availability
              </button>
            </div>
            </div>
            
            <div className="p-6 space-y-4">
              {activeTab === 'details' && (
                <div className="space-y-4">
                  {/* --- ORIGINAL PRODUCT DETAILS INPUTS --- */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                    <input type="text" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Enter product name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <select value={newProduct.language} onChange={(e) => setNewProduct({ ...newProduct, language: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="EN">English</option>
                      <option value="ZH">Chinese</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
                    <select value={newProduct.product_kind} onChange={(e) => setNewProduct({ ...newProduct, product_kind: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="booking">Booking</option>
                      <option value="discount">Discount</option>
                      <option value="credit">Credit</option>
                      <option value="pass">Pass</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Enter product description" rows="3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input type="number" value={newProduct.unit_price} onChange={(e) => setNewProduct({ ...newProduct, unit_price: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Enter price" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select value={newProduct.currency} onChange={(e) => setNewProduct({ ...newProduct, currency: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="HK$">HK$</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="IDR">IDR</option>
                    </select>
                  </div>
                </div>
              )} {activeTab === 'benefits' && (
                <div className="space-y-6">
                  {/* --- BENEFITS TAB --- */}
                  
                  {/* Time Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Time Credit</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount</label>
                        <input type="number" min="0" className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.time_credit?.amount || 0} onChange={e => updateBenefit('time_credit', 'amount', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit</label>
                        <select className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.time_credit?.unit || 'minutes'} onChange={e => updateBenefit('time_credit', 'unit', e.target.value)}>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newProduct.benefits?.time_credit?.never_expire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" value={newProduct.benefits?.time_credit?.expiry_days || 0} onChange={e => updateBenefit('time_credit', 'expiry_days', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm">
                        <input type="checkbox" checked={newProduct.benefits?.time_credit?.never_expire || false} onChange={e => updateBenefit('time_credit', 'never_expire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => {
                        const resourceId = res.id || res._id;
                        return (
                          <label key={resourceId} className="flex items-center space-x-2 text-sm">
                            <input type="checkbox" checked={newProduct.benefits?.time_credit?.applicable_resources?.includes(resourceId) || false} onChange={() => handleResourceToggle('time_credit', resourceId)} />
                            <span>{res.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Money Credit */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Money Credit</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.money_credit?.amount || 0} onChange={e => updateBenefit('money_credit', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newProduct.benefits?.money_credit?.never_expire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" value={newProduct.benefits?.money_credit?.expiry_days || 0} onChange={e => updateBenefit('money_credit', 'expiry_days', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm">
                        <input type="checkbox" checked={newProduct.benefits?.money_credit?.never_expire || false} onChange={e => updateBenefit('money_credit', 'never_expire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Applicable Resources</label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border bg-white rounded">
                      {resources.map(res => {
                        const resourceId = res.id || res._id;
                        return (
                          <label key={resourceId} className="flex items-center space-x-2 text-sm">
                            <input type="checkbox" checked={newProduct.benefits?.money_credit?.applicable_resources?.includes(resourceId) || false} onChange={() => handleResourceToggle('money_credit', resourceId)} />
                            <span>{res.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Passes */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Passes</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Count</label>
                        <input type="number" min="0" disabled={newProduct.benefits?.passes?.is_unlimited} className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.passes?.count || 0} onChange={e => updateBenefit('passes', 'count', Number(e.target.value))} />
                      </div>
                      <div className="flex items-end pb-2">
                        <label className="flex items-center space-x-2 text-sm">
                          <input type="checkbox" checked={newProduct.benefits?.passes?.is_unlimited || false} onChange={e => updateBenefit('passes', 'is_unlimited', e.target.checked)} />
                          <span>Unlimited Passes</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newProduct.benefits?.passes?.never_expire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" value={newProduct.benefits?.passes?.expiry_days || 0} onChange={e => updateBenefit('passes', 'expiry_days', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm">
                        <input type="checkbox" checked={newProduct.benefits?.passes?.never_expire || false} onChange={e => updateBenefit('passes', 'never_expire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 mt-4">Access Window</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.passes?.access_window?.start_time || ''} onChange={e => updatePassWindow('start_time', e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Time</label>
                        <input type="time" className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.passes?.access_window?.end_time || ''} onChange={e => updatePassWindow('end_time', e.target.value)} />
                      </div>
                    </div>
                    <label className="block text-xs text-gray-500 mb-1">Valid Days</label>
                    <div className="flex flex-wrap gap-3 p-2 bg-white border rounded">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <label key={day} className="flex items-center space-x-1 text-sm">
                          <input type="checkbox" checked={newProduct.benefits?.passes?.access_window?.days?.includes(day)} onChange={(e) => {
                            const currentDays = newProduct.benefits?.passes?.access_window?.days || [];
                            updatePassWindow('days', e.target.checked ? [...currentDays, day] : currentDays.filter(d => d !== day));
                          }}/>
                          <span>{day.substring(0,3)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Printing Credits */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Printing Credits</h3>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                      <input type="number" min="0" className="w-full px-3 py-2 border rounded" value={newProduct.benefits?.printing_credits?.amount || 0} onChange={e => updateBenefit('printing_credits', 'amount', Number(e.target.value))} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Expiry (Days)</label>
                        <input type="number" min="1" disabled={newProduct.benefits?.printing_credits?.never_expire} className="w-full px-3 py-2 border rounded disabled:bg-gray-200" value={newProduct.benefits?.printing_credits?.expiry_days || 0} onChange={e => updateBenefit('printing_credits', 'expiry_days', Number(e.target.value))} />
                      </div>
                      <label className="flex items-center space-x-2 text-sm">
                        <input type="checkbox" checked={newProduct.benefits?.printing_credits?.never_expire || false} onChange={e => updateBenefit('printing_credits', 'never_expire', e.target.checked)} />
                        <span>Never Expire</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'availability' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Type</label>
                    <select value={newProduct.availability?.purchase_type} onChange={(e) => updateAvailability('purchase_type', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="one_off">One Off</option>
                      <option value="recurring">Recurring</option>
                    </select>
                  </div>

                  {newProduct.availability?.purchase_type === 'recurring' && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                        <select value={newProduct.availability?.recurring_config?.frequency} onChange={(e) => updateRecurringConfig('frequency', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recurring Charge Amount</label>
                        <input type="number" value={newProduct.availability?.recurring_config?.charge_amount} onChange={(e) => updateRecurringConfig('charge_amount', e.target.value)} placeholder="Enter amount" className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Method</label>
                    <select value={newProduct.availability?.purchase_method} onChange={(e) => updateAvailability('purchase_method', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="buy_alone_or_with_plan">Buy Alone or With Plan</option>
                      <option value="buy_alone_only">Buy Alone Only</option>
                      <option value="buy_only_if_plan_exists">Buy Only If Plan Exists</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Customers</label>
                    <select value={newProduct.availability?.allowed_customers} onChange={(e) => updateAvailability('allowed_customers', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="all">All Customers</option>
                      <option value="contacts">Contacts Only</option>
                      <option value="members">Members Only</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowAddProduct(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddProduct}
                disabled={!isFormValid()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {showDetailView && selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => {
            setShowDetailView(false);
            setSelectedProduct(null);
            setActiveDropdown(null);
          }}
          onSave={() => fetchProducts(false)}
        />
      )}
    </div>
  );
}